import { useState, useMemo } from 'react';
import type { Column, ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button } from '@design-system';
import { Download, ChevronRight, BarChart3 } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { SortHeader, renderHeaderCell } from '@/components/DataTableColumn';
import { useSearchParams } from 'react-router-dom';
import { MetricsAppliedFilters, MetricsOrganizationFilter } from './metrics-date-filter';
import type {
  MetricsOrganization,
  TransformedSubscription,
  TransformedSubscriptionSeat,
} from '../types/metrics-type';
import { buildVisibleColumnsCsv } from './visible-columns-csv';

interface SubscriptionDetailsTableProps {
  getFilteredSubscriptions: (filterKey?: string | null) => TransformedSubscription[];
  isLoading: boolean;
  filterKey?: string | null;
  organization?: MetricsOrganization;
  onOrganizationChange?: (organization: MetricsOrganization) => void;
  onCheckUsage?: (filterKey: string | null, organization: MetricsOrganization) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const subscriptionColumnHelper = createColumnHelper<TransformedSubscription>();
const seatColumnHelper = createColumnHelper<TransformedSubscriptionSeat>();

export function buildSubscriptionSeatsCsv(
  seats: TransformedSubscriptionSeat[],
  visibleColumns: Column<TransformedSubscriptionSeat, unknown>[],
) {
  return buildVisibleColumnsCsv(seats, visibleColumns, (value, column) =>
    value && (column.id === 'seatCreatedDate' || column.id === 'seatUpdatedDate')
      ? new Date(String(value)).toLocaleDateString()
      : value,
  );
}

export function getSubscriptionSeatDisplayEmail(
  seat: Pick<TransformedSubscriptionSeat, 'advisorEmail' | 'invitedEmail'>,
) {
  return seat.advisorEmail || seat.invitedEmail || 'N/A';
}

export function getSubscriptionRowId(
  subscription: Pick<
    TransformedSubscription,
    'ownerAdvisorId' | 'createdDateTime' | 'subscriptionStatus'
  >,
) {
  return [
    subscription.ownerAdvisorId,
    subscription.createdDateTime,
    subscription.subscriptionStatus,
  ].join('::');
}

export function SubscriptionDetailsTable({
  getFilteredSubscriptions,
  isLoading,
  filterKey,
  organization,
  onOrganizationChange,
  onCheckUsage,
  onRefresh,
  isRefreshing = false,
}: SubscriptionDetailsTableProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null);
  const [seatSearchText, setSeatSearchText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const clearFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('filter');
    nextParams.delete('from');
    nextParams.delete('to');
    setSearchParams(nextParams);
  };

  const checkUsage = () => {
    onCheckUsage?.(filterKey ?? null, organization ?? 'all');
  };

  const changeOrganization = (nextOrganization: MetricsOrganization) => {
    onOrganizationChange?.(nextOrganization);
    const nextParams = new URLSearchParams(searchParams);
    if (nextOrganization === 'nyl') nextParams.set('partner', 'nyl');
    else nextParams.delete('partner');
    setSearchParams(nextParams);
  };

  const filteredSubscriptions = useMemo(() => {
    return getFilteredSubscriptions(filterKey);
  }, [filterKey, getFilteredSubscriptions]);
  const selectedSubscription = useMemo(
    () =>
      filteredSubscriptions.find(
        subscription => getSubscriptionRowId(subscription) === selectedSubscriptionId,
      ) ?? null,
    [filteredSubscriptions, selectedSubscriptionId],
  );

  const toggleSubscription = (subscription: TransformedSubscription) => {
    const subscriptionId = getSubscriptionRowId(subscription);
    setSelectedSubscriptionId(currentId => (currentId === subscriptionId ? null : subscriptionId));
    setSeatSearchText('');
  };

  const exportSubscriptionsToCsv = (visibleColumns: Column<TransformedSubscription, unknown>[]) => {
    const csv = buildVisibleColumnsCsv(filteredSubscriptions, visibleColumns, (value, column) => {
      if (column.id === 'createdDate') return new Date(String(value)).toLocaleDateString();
      if (column.id === 'nextBilling') {
        return value ? new Date(String(value)).toLocaleDateString() : 'N/A';
      }
      return value;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const exportSeatsToCsv = (
    subscription: TransformedSubscription,
    visibleColumns: Column<TransformedSubscriptionSeat, unknown>[],
  ) => {
    const csv = buildSubscriptionSeatsCsv(subscription.seats ?? [], visibleColumns);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscription-seats-${subscription.ownerAdvisorId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const getStatusBadgeVariant = (
    status: string,
  ): 'default' | 'destructive' | 'secondary' | 'outline' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'canceled':
      case 'cancelled':
        return 'destructive';
      case 'past_due':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getSeatStatusBadgeVariant = (
    status: string,
  ): 'default' | 'destructive' | 'secondary' | 'outline' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'invited':
        return 'secondary';
      case 'inactive':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const subscriptionColumnDefinitions = [
    subscriptionColumnHelper.display({
      id: 'expand',
      header: () => renderHeaderCell(''),
      cell: info => {
        const row = info.row.original;
        const hasSeats = row.seats && row.seats.length > 0;
        const isSelected = selectedSubscriptionId === getSubscriptionRowId(row);

        return hasSeats ? (
          <button
            type="button"
            aria-label={`${isSelected ? 'Collapse' : 'Expand'} subscription details for ${row.ownerAdvisorName}`}
            aria-expanded={isSelected}
            onClick={e => {
              e.stopPropagation();
              toggleSubscription(row);
            }}
            className="rounded p-1 hover:bg-gray-100"
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform ${isSelected ? 'rotate-90' : ''}`}
            />
          </button>
        ) : null;
      },
      size: 44,
    }),
    subscriptionColumnHelper.accessor((row: any) => row.ownerAdvisorName || row.ownerAdvisorId, {
      id: 'ownerAdvisor',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Owner Advisor
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        const isSelected = selectedSubscriptionId === getSubscriptionRowId(row);
        return (
          <span className={`font-medium ${isSelected ? 'text-blue-600' : ''}`}>
            {info.getValue()}
          </span>
        );
      },
      enableSorting: true,
      meta: { label: 'Owner Advisor' },
      size: 190,
    }),
    subscriptionColumnHelper.accessor('subscriptionStatus', {
      id: 'status',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Status
          </SortHeader>,
        ),
      cell: info => (
        <Badge variant={getStatusBadgeVariant(info.getValue())}>{info.getValue()}</Badge>
      ),
      enableSorting: true,
      meta: { label: 'Status' },
      size: 100,
    }),
    subscriptionColumnHelper.accessor('totalSeats', {
      id: 'totalSeats',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Total Seats
          </SortHeader>,
        ),
      cell: info => <span className="font-medium">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Total Seats' },
      size: 110,
    }),
    subscriptionColumnHelper.accessor(
      row => row.seats?.filter(seat => seat.status === 'active').length || 0,
      {
        id: 'utilizedSeats',
        header: ({ column }) =>
          renderHeaderCell(
            <SortHeader
              column={column}
              variant="ghost"
            >
              Utilized Seats
            </SortHeader>,
          ),
        cell: info => {
          const row = info.row.original;
          const utilized = info.getValue();
          const total = row.totalSeats;
          const percentage = total > 0 ? Math.round((utilized / total) * 100) : 0;
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {utilized} / {total}
              </span>
              <span className="text-xs text-gray-500">({percentage}%)</span>
            </div>
          );
        },
        enableSorting: true,
        meta: { label: 'Utilized Seats' },
        size: 150,
      },
    ),
    subscriptionColumnHelper.accessor('billingInterval', {
      id: 'billingInterval',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Billing Interval
          </SortHeader>,
        ),
      cell: info => <span className="text-sm capitalize text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Billing Interval' },
      size: 145,
    }),
    subscriptionColumnHelper.accessor('createdDateTime', {
      id: 'createdDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Created Date
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return <span className="text-sm">{new Date(value).toLocaleDateString()}</span>;
      },
      enableSorting: true,
      meta: { label: 'Created Date' },
      size: 125,
    }),
    subscriptionColumnHelper.accessor('nextBillingDateTime', {
      id: 'nextBilling',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Next Billing
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">{value ? new Date(value).toLocaleDateString() : 'N/A'}</span>
        );
      },
      enableSorting: true,
      meta: { label: 'Next Billing' },
      size: 125,
    }),
    subscriptionColumnHelper.accessor('cancellationTriggered', {
      id: 'cancellationTriggered',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Cancellation Triggered
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return <Badge variant={value === 'Yes' ? 'destructive' : 'secondary'}>{value}</Badge>;
      },
      enableSorting: true,
      meta: { label: 'Cancellation Triggered' },
      size: 230,
    }),
    subscriptionColumnHelper.accessor('cancellationTriggeredAt', {
      id: 'cancellationTriggeredAt',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Cancellation At
          </SortHeader>,
        ),
      cell: info => <span className="block truncate text-sm">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Cancellation At' },
      size: 180,
    }),
    subscriptionColumnHelper.accessor('cancellationTriggeredReason', {
      id: 'cancellationReason',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Cancellation Reason
          </SortHeader>,
        ),
      cell: info => (
        <span
          className="block truncate text-sm"
          title={info.getValue()}
        >
          {info.getValue()}
        </span>
      ),
      enableSorting: true,
      meta: { label: 'Cancellation Reason' },
      size: 220,
    }),
    subscriptionColumnHelper.accessor('additionalFeedback', {
      id: 'additionalFeedback',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Additional Feedback
          </SortHeader>,
        ),
      cell: info => (
        <span
          className="block truncate text-sm"
          title={info.getValue()}
        >
          {info.getValue()}
        </span>
      ),
      enableSorting: true,
      meta: { label: 'Additional Feedback' },
      size: 220,
    }),
  ];

  const seatColumnDefinitions = [
    seatColumnHelper.accessor(seat => getSubscriptionSeatDisplayEmail(seat), {
      id: 'email',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Email
          </SortHeader>,
        ),
      cell: info => <span className="font-medium">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Email' },
    }),
    seatColumnHelper.accessor('advisorId', {
      id: 'advisorId',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Advisor ID
          </SortHeader>,
        ),
      cell: info => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Advisor ID' },
    }),
    seatColumnHelper.accessor(row => row.advisorName || 'Unknown', {
      id: 'advisorName',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Advisor Name
          </SortHeader>,
        ),
      cell: info => (
        <div className="flex items-center gap-2">
          <span className="text-sm">{info.getValue()}</span>
          {info.row.original.isOwner && (
            <Badge
              variant="outline"
              className="text-xs"
            >
              Owner
            </Badge>
          )}
        </div>
      ),
      enableSorting: true,
      meta: { label: 'Advisor Name' },
    }),
    seatColumnHelper.accessor(row => row.advisorOrganization || 'Unknown', {
      id: 'advisorOrganization',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Organization
          </SortHeader>,
        ),
      cell: info => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Organization' },
    }),
    seatColumnHelper.accessor('status', {
      id: 'seatStatus',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Status
          </SortHeader>,
        ),
      cell: info => (
        <Badge variant={getSeatStatusBadgeVariant(info.getValue())}>{info.getValue()}</Badge>
      ),
      enableSorting: true,
      meta: { label: 'Status' },
    }),
    seatColumnHelper.accessor('createdDateTime', {
      id: 'seatCreatedDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Created Date
          </SortHeader>,
        ),
      cell: info => (
        <span className="text-sm">{new Date(info.getValue()).toLocaleDateString()}</span>
      ),
      enableSorting: true,
      meta: { label: 'Created Date' },
    }),
    seatColumnHelper.accessor('updatedDateTime', {
      id: 'seatUpdatedDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Updated Date
          </SortHeader>,
        ),
      cell: info => (
        <span className="text-sm">{new Date(info.getValue()).toLocaleDateString()}</span>
      ),
      enableSorting: true,
      meta: { label: 'Updated Date' },
    }),
  ];

  return (
    <div className="space-y-6">
      {/* Subscriptions DataTable */}
      <DataTable
        columns={subscriptionColumnDefinitions as ColumnDef<TransformedSubscription>[]}
        data={filteredSubscriptions}
        tableId="subscription-details-table"
        getRowId={getSubscriptionRowId}
        isLoading={isLoading || isRefreshing}
        handleRefresh={onRefresh}
        variant="outlined"
        initialColumnVisibility={{
          totalSeats: false,
          cancellationTriggered: false,
          cancellationTriggeredAt: false,
          cancellationReason: false,
          additionalFeedback: false,
        }}
        initialSorting={[{ id: 'createdDate', desc: true }]}
        pageSize={25}
        globalSearchText={searchText}
        handleSearchTextChange={setSearchText}
        globalSearchFieldKeys={[
          'ownerAdvisor',
          'ownerAdvisorId',
          'ownerAdvisorEmail',
          'status',
          'billingInterval',
        ]}
        globalSearchPlaceholder="Search subscriptions..."
        stackToolbarUntilDesktop
        stickyHeaderOnPageScroll
        stickyToolbarOnPageScroll
        stickyToolbarTopOffset="var(--metrics-sticky-table-top)"
        horizontalScrollOnOverflow
        alwaysShowPagination
        onRowClick={subscription => {
          if (subscription.seats?.length) toggleSubscription(subscription);
        }}
        renderExpandedRow={subscription => {
          if (
            selectedSubscriptionId !== getSubscriptionRowId(subscription) ||
            !subscription.seats?.length
          ) {
            return null;
          }

          return (
            <div
              className="space-y-4 p-5"
              data-testid={`subscription-details-${getSubscriptionRowId(subscription)}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h4 className="font-semibold text-gray-900">
                  Subscription Seats - {subscription.ownerAdvisorEmail}
                </h4>
                <Button
                  type="button"
                  onClick={() => setSelectedSubscriptionId(null)}
                  variant="ghost"
                  size="sm"
                >
                  Close
                </Button>
              </div>
              <DataTable
                columns={seatColumnDefinitions as ColumnDef<TransformedSubscriptionSeat>[]}
                data={subscription.seats}
                tableId="subscription-seats-table"
                getRowId={row => `${row.advisorId}-${row.invitedEmail}`}
                isLoading={isLoading}
                variant="outlined"
                initialSorting={[{ id: 'seatCreatedDate', desc: true }]}
                pageSize={10}
                globalSearchText={seatSearchText}
                handleSearchTextChange={setSeatSearchText}
                globalSearchFieldKeys={['email', 'advisorId', 'seatStatus']}
                globalSearchPlaceholder="Search seats..."
                stickyHeaderOnPageScroll
                stickyHeaderTopClassName="top-[var(--metrics-sticky-table-top)]"
                headerExtras={table => (
                  <Button
                    onClick={() => exportSeatsToCsv(subscription, table.getVisibleLeafColumns())}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export Seats CSV
                  </Button>
                )}
              />
            </div>
          );
        }}
        headerExtras={table => (
          <div className="flex flex-wrap items-center gap-2">
            <MetricsAppliedFilters
              filterKey={filterKey}
              onRemoveFilter={clearFilter}
            />
            {organization !== undefined && onOrganizationChange && (
              <MetricsOrganizationFilter
                organization={organization}
                onOrganizationChange={changeOrganization}
                idPrefix="subscriptions"
                compact
              />
            )}
            {((filterKey && filterKey !== 'all') || organization === 'nyl') && (
              <Button
                onClick={checkUsage}
                variant="outline"
                size="sm"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Check Usage
              </Button>
            )}
            <Button
              onClick={() => exportSubscriptionsToCsv(table.getVisibleLeafColumns())}
              variant="outline"
              size="sm"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        )}
      />
    </div>
  );
}
